/* =============================================================================
   CORE / LIVE-VIEW-REGISTRY — automatische Refresh-Verkabelung für Tab-Views
   =============================================================================
   v0.31.3 / Session 22 Nachschlag / Phase 23
   ----------------------------------------------------------------------------
   ZWECK: ein projektweites, systematisches Pattern für „selbst-aktualisierende"
   Tab-Sichten. Jede neue Cockpit-View, die auf Cache-Daten reagiert (orders,
   reservations, dashboard, archive, audit-log, stats, …) muss sich hier nur
   EINMAL registrieren und ist automatisch live.

   PROBLEM, das hier gelöst wird (Memory: feedback_gg_no_view_hijack):
   Realtime-Events (`order:status_changed` etc.) erreichten den Cockpit-Tab
   bisher nur, wenn dieser GERADE SICHTBAR war. Wechsel KDS → Cockpit ließ
   den Bestellungen-Tab mit veraltetem DOM zurück — Karte in falscher Spalte,
   alte Sub-Status, „in Zubereitung" obwohl „Bereit".

   ARCHITEKTUR — drei Trigger pro registrierter View:

     A) Realtime-Event kommt + Tab IST sichtbar  →  render direkt
     B) Realtime-Event kommt + Tab IST verborgen →  markiere als „dirty"
     C) Tab wird sichtbar (View-Switch / Browser-visibility / Subtab-Wechsel)
        →  wenn dirty: render

   Verwendung:

     import { registerLiveView } from '../../core/live-view-registry.js';
     registerLiveView({
       tabId: 'tab-orders',
       render: renderOrders,
       events: ['order:realtime', 'order:status_changed',
                'order:created', 'order:cancelled']
     });

   Das war's. Kein eigener Event-Listener-Boilerplate mehr in Tab-Modulen.
============================================================================ */

const _liveViews = new Map();   // tabId → { render, events, dirty }
let _initialized = false;

/**
 * Registriert eine Tab-View für automatischen Live-Refresh. Idempotent —
 * mehrfaches Registrieren mit gleicher tabId überschreibt nur die Konfig.
 */
export function registerLiveView({ tabId, render, events = [] }) {
  if (!tabId || typeof render !== 'function') {
    console.warn('[live-view-registry] registerLiveView: tabId + render() erforderlich.');
    return;
  }
  ensureInitialized();
  const existing = _liveViews.get(tabId);
  if (existing) {
    // Reconfigure (Events ggf. erweitern, render-fn updaten)
    existing.render = render;
    for (const ev of events) {
      if (!existing.events.includes(ev)) {
        existing.events.push(ev);
        _wireEvent(ev);
      }
    }
    return;
  }
  _liveViews.set(tabId, { render, events: events.slice(), dirty: false });
  for (const ev of events) _wireEvent(ev);
}

/**
 * Markiert eine Tab-View als „dirty" — der nächste „became-visible"-Trigger
 * rendert sie. Kann auch manuell aufgerufen werden (z.B. nach lokaler
 * Modifikation, die nicht über App.events lief).
 */
export function markLiveViewDirty(tabId) {
  const view = _liveViews.get(tabId);
  if (view) view.dirty = true;
}

/**
 * Innere Behandlung: bei einem Event prüft jede registrierte View, ob sie
 * sich angeschlossen hat. Sichtbare rendern sofort, unsichtbare markieren.
 */
const _wiredEvents = new Set();
function _wireEvent(eventName) {
  if (_wiredEvents.has(eventName)) return;
  _wiredEvents.add(eventName);
  if (!window.App || !window.App.events) return;
  window.App.events.on(eventName, () => {
    for (const [tabId, view] of _liveViews.entries()) {
      if (!view.events.includes(eventName)) continue;
      if (_isTabVisible(tabId)) {
        try { view.render(); view.dirty = false; }
        catch (e) { console.warn('[live-view-registry] render failed:', tabId, e); }
      } else {
        view.dirty = true;
      }
    }
  });
}

/**
 * Sichtbarkeit eines Tab-Containers prüfen — DOM-basiert, robust gegen
 * Subtab-Selektor-Inkonsistenzen oder hash-Routing-Änderungen.
 */
function _isTabVisible(tabId) {
  const el = document.getElementById(tabId);
  if (!el) return false;
  if (el.style.display === 'none') return false;
  if (el.offsetParent === null) return false;
  return true;
}

/**
 * Beim Sichtbarwerden einer View — durch View-Switch, Subtab-Switch oder
 * Browser-Visibility — schauen wir alle dirty-Views durch und rendern die,
 * die jetzt sichtbar sind.
 */
function _onBecameVisible() {
  for (const [tabId, view] of _liveViews.entries()) {
    if (view.dirty && _isTabVisible(tabId)) {
      try { view.render(); view.dirty = false; }
      catch (e) { console.warn('[live-view-registry] became-visible render failed:', tabId, e); }
    }
  }
}

function ensureInitialized() {
  if (_initialized) return;
  _initialized = true;

  // Trigger C-1: Browser-Tab wird wieder sichtbar (User wechselt vom anderen
  // Browser-Tab zurück). Während Tab im Hintergrund war können Realtime-
  // Events angekommen sein die nicht gerendert wurden.
  try {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') _onBecameVisible();
    });
  } catch (_) { /* SSR-Schutz */ }

  // Trigger C-2: App-internes Event von view-switch.js, wenn der User
  // im SPA zwischen Views (admin/kds/bar/customer) oder zwischen Subtabs
  // wechselt. view-switch.js emittet `view:became-visible` mit { view, tab }.
  if (window.App && window.App.events) {
    window.App.events.on('view:became-visible', () => {
      // micro-defer, damit das DOM die display:block-Änderung schon übernommen hat
      setTimeout(_onBecameVisible, 0);
    });
  }
}

// Backward-Compat: window-Bridge für inline-Handler / Debug-Konsole.
if (typeof window !== 'undefined') {
  window.LiveViewRegistry = {
    registerLiveView,
    markLiveViewDirty,
    _internalViews: _liveViews     // nur für Debug
  };
}
